import { FormControl, FormControlLabel, InputLabel, Select, Switch, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { DeliveryPointDetails } from '../../types/delivery-point-details.type';
import { DeliveryPointType } from '../../types/delivery-point.type';
import { DeliveryPointFieldChange } from '../../hooks/use-delivery-point-edit-form.hook';
import { DELIVERY_POINT_TYPES, DELIVERY_POINT_TYPE_LABELS } from '../../constants/delivery-point-type.constants';
import { BOUNDED_TEXT_ERROR, isBoundedTextInvalid, isCompanyType } from '../../utils/delivery-point-form-rules.util';
import { DeliveryPointCard } from './DeliveryPointCard.component';

type Props = {
  updatedDeliveryPoint: DeliveryPointDetails;
  isArchived: boolean;
  codeError?: string;
  onFieldChange: (change: DeliveryPointFieldChange) => void;
  onTypeChange: (type: DeliveryPointType) => void;
};

const useStyles = makeStyles((theme) => ({
  row: {
    display: 'flex',
    gap: '1rem',

    '& > *': {
      flex: 1,
    },
  },
  switchBase: {
    '&$switchChecked': {
      color: theme.palette.admin.primary.main,
    },
    '&$switchChecked + $switchTrack': {
      backgroundColor: theme.palette.admin.primary.main,
    },
  },
  switchChecked: {},
  switchTrack: {},
}));

export function DeliveryPointIdentityCard({
  updatedDeliveryPoint,
  isArchived,
  codeError,
  onFieldChange,
  onTypeChange,
}: Props) {
  const classes = useStyles();
  const isNameInvalid = updatedDeliveryPoint.name.length === 0;
  const isDescriptionInvalid = isBoundedTextInvalid(updatedDeliveryPoint.description ?? '');
  const isCompany = isCompanyType(updatedDeliveryPoint.type);

  return (
    <DeliveryPointCard title="Identité">
      <FormControlLabel
        control={
          <Switch
            size="small"
            color="default"
            classes={{ switchBase: classes.switchBase, checked: classes.switchChecked, track: classes.switchTrack }}
            disabled={isArchived}
            checked={updatedDeliveryPoint.isActive}
            onChange={(event) => onFieldChange({ field: 'isActive', value: event.target.checked })}
          />
        }
        label="Actif"
      />
      <TextField
        id="delivery-point-name"
        label="Nom"
        type="text"
        size="small"
        variant="outlined"
        fullWidth
        error={isNameInvalid}
        helperText={isNameInvalid ? 'Le nom est obligatoire' : undefined}
        value={updatedDeliveryPoint.name}
        onChange={(event) => onFieldChange({ field: 'name', value: event.target.value })}
      />
      <div className={classes.row}>
        <FormControl variant="outlined" size="small">
          <InputLabel htmlFor="delivery-point-type">Type</InputLabel>
          <Select
            native
            label="Type"
            value={updatedDeliveryPoint.type}
            inputProps={{ id: 'delivery-point-type' }}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
              onTypeChange(event.target.value as DeliveryPointType)
            }
          >
            {DELIVERY_POINT_TYPES.map((type) => (
              <option key={type} value={type}>
                {DELIVERY_POINT_TYPE_LABELS[type]}
              </option>
            ))}
          </Select>
        </FormControl>
        {isCompany && (
          <TextField
            id="delivery-point-code"
            label="Code entreprise"
            type="text"
            size="small"
            variant="outlined"
            inputProps={{ maxLength: 20 }}
            error={codeError !== undefined}
            helperText={codeError}
            value={updatedDeliveryPoint.code ?? ''}
            onChange={(event) => onFieldChange({ field: 'code', value: event.target.value })}
          />
        )}
      </div>
      <TextField
        id="delivery-point-description"
        label="Description"
        type="text"
        size="small"
        variant="outlined"
        fullWidth
        multiline
        minRows={2}
        error={isDescriptionInvalid}
        helperText={isDescriptionInvalid ? BOUNDED_TEXT_ERROR : undefined}
        value={updatedDeliveryPoint.description ?? ''}
        onChange={(event) => onFieldChange({ field: 'description', value: event.target.value })}
      />
    </DeliveryPointCard>
  );
}
